import classNames from 'classnames';
import React, { useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Button from '@/components/molecules/button/button';
import styles from './alert-banner.module.scss';

export type AlertBannerVariants =
  | 'attention'
  | 'highVis'
  | 'neutral'
  | 'warning';

export interface AlertBannerProps {
  /** The content to render as a React node */
  children?: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** Whether to show the close button */
  hasCloseButton?: boolean;
  /** The rich text to show */
  text?: string;
  /** The variant of the alert banner */
  variant?: AlertBannerVariants;
}

const AlertBanner = ({
  children,
  className,
  hasCloseButton = false,
  text,
  variant = 'attention',
}: AlertBannerProps) => {
  const [shouldShow, setShouldShow] = useState(true);

  const handleClose = () => {
    setShouldShow(false);
  };

  if (!shouldShow) return;

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.attention]: variant === 'attention',
        [styles.highVis]: variant === 'highVis',
        [styles.neutral]: variant === 'neutral',
        [styles.warning]: variant === 'warning',
      })}
    >
      <div className={styles.iconContainer}>
        <Icon name="Warning" size={24} />
      </div>

      {text && (
        <div className={styles.textContainer}>
          <RichText text={text} />
        </div>
      )}

      {children && <div className={styles.contentContainer}>{children}</div>}

      {hasCloseButton && (
        <Button
          className={styles.button}
          icon={'Close'}
          onClick={handleClose}
          variant="ghost"
        />
      )}
    </div>
  );
};

AlertBanner.displayName = 'AlertBanner';

export default AlertBanner;
